/**
 * FairwayBid – gemeinsame Bausteine für druckfertige Dokumente
 *
 * Vergabevermerk und Angebots-PDF sollen aussehen, als kämen sie aus einem
 * Haus. Deshalb liegen Textmaskierung, Datumsformat und das Blatt-Layout
 * hier an einer Stelle statt zweimal nebeneinander.
 *
 * Reine Zeichenkettenarbeit, kein React – damit prüfbar.
 */

export const escapeHtml = value =>
  String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const formatDate = value => {
  if (!value) return '–';
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime())
    ? '–'
    : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Anzeigename des Anbieters einer Transaktion. */
export const providerName = tx => tx?.provider?.attributes?.profile?.displayName || 'Anbieter';

/** Anzeigename der ausschreibenden Anlage einer Transaktion. */
export const customerName = tx => tx?.customer?.attributes?.profile?.displayName || 'Golfanlage';

/** Gemeinsames Blatt-Layout für alle Druckdokumente. */
export const BASE_STYLES = `
  @page { size: A4; margin: 20mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
         color: #12212f; line-height: 1.5; font-size: 11pt; margin: 0; }
  h1 { font-size: 18pt; margin: 0 0 4px 0; }
  .sub { color: #5a6a78; margin: 0 0 24px 0; }
  h2 { font-size: 13pt; margin: 24px 0 8px 0; }
  dl { display: grid; grid-template-columns: 200px 1fr; gap: 4px 16px; margin: 0; }
  dt { color: #5a6a78; }
  dd { margin: 0; }
  table { border-collapse: collapse; width: 100%; margin-top: 8px; font-size: 10pt; }
  th, td { border: 1px solid #d4dce3; padding: 6px 8px; text-align: left; vertical-align: top; }
  thead th { background: #f2f5f7; }
  th.crit { background: #f2f5f7; font-weight: normal; color: #5a6a78; width: 190px; }
  .decision { background: #f2f8f3; border-left: 3px solid #1d6b2c; padding: 12px 14px; margin-top: 8px; }
  footer { margin-top: 32px; color: #5a6a78; font-size: 9pt; border-top: 1px solid #d4dce3; padding-top: 8px; }
  @media print { .noprint { display: none; } }
`;

/** Der Knopf steht unter dem Dokument und verschwindet im Ausdruck. */
export const PRINT_BUTTON = `
  <p class="noprint" style="margin-top:24px">
    <button type="button" onclick="window.print()">Drucken oder als PDF sichern</button>
  </p>`;

/**
 * Setzt ein vollständiges HTML-Dokument zusammen.
 *
 * @param {Object} params
 * @param {string} params.title Titel im Fenster und in der Kopfzeile der Datei
 * @param {string} params.body Inhalt zwischen <body> und dem Druckknopf
 * @param {string} params.extraStyles zusätzliche Regeln nach BASE_STYLES
 * @returns {string} vollständiges HTML-Dokument
 */
export const buildDocument = ({ title, body, extraStyles = '' } = {}) => `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>${BASE_STYLES}${extraStyles}</style>
</head>
<body>
${body}
${PRINT_BUTTON}
</body>
</html>`;
